/**
 * Enhanced cookie management system for PPC analyzer opt-in functionality
 */

export interface CookieOptions {
  expires?: number; // Days from now
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Sets a cookie with the specified name, value, and options
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  try {
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    
    if (options.expires) {
      const date = new Date();
      date.setTime(date.getTime() + (options.expires * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }
    
    if (options.path) {
      cookieString += `; path=${options.path}`;
    }
    
    if (options.domain) {
      cookieString += `; domain=${options.domain}`;
    }
    
    if (options.secure) {
      cookieString += `; secure`;
    }
    
    if (options.sameSite) {
      cookieString += `; samesite=${options.sameSite}`;
    }
    
    document.cookie = cookieString;
  } catch (error) {
    console.warn('Failed to set cookie:', error);
  }
};

/**
 * Gets a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  try {
    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to get cookie:', error);
    return null;
  }
};

/**
 * Deletes a cookie by setting its expiration to the past
 */
export const deleteCookie = (name: string, path: string = '/'): void => {
  try {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
  } catch (error) {
    console.warn('Failed to delete cookie:', error);
  }
};

/**
 * Checks if a cookie exists and is not expired
 */
export const cookieExists = (name: string): boolean => {
  return getCookie(name) !== null;
};

// PPC-specific cookie management functions

const CORTEXIQ_PROMO_COOKIE = 'cortexiq_promo_v1';

/**
 * Sets the waitlist seen cookie (30 days)
 */
export const setWaitlistSeenCookie = (): void => {
  setCookie(CORTEXIQ_PROMO_COOKIE, 'true', { expires: 30, path: '/' });
};

/**
 * Checks if user has seen the waitlist landing page
 */
export const hasSeenWaitlistLanding = (): boolean => {
  return cookieExists(CORTEXIQ_PROMO_COOKIE);
};

/**
 * Sets the waitlist opted-in cookie (permanent - 10 years)
 */
export const setWaitlistOptedInCookie = (): void => {
  setCookie('ppc_waitlist_opted_in', 'true', { expires: 3650, path: '/' });
};

/**
 * Checks if user has opted into the waitlist
 */
export const hasOptedIntoWaitlist = (): boolean => {
  return cookieExists('ppc_waitlist_opted_in');
};

/**
 * Sets the results popup shown cookie (7 days)
 */
export const setResultsPopupShownCookie = (): void => {
  setCookie('ppc_results_popup_shown', Date.now().toString(), { expires: 7, path: '/' });
};

/**
 * Checks if results popup was shown recently (within 7 days)
 */
export const wasResultsPopupShownRecently = (): boolean => {
  const timestamp = getCookie('ppc_results_popup_shown');
  if (!timestamp) return false;
  
  const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const popupTime = parseInt(timestamp, 10);
  
  return (now - popupTime) < sevenDaysInMs;
};

/**
 * Checks if results popup should be shown
 * Returns true if:
 * - User hasn't opted into waitlist
 * - Popup hasn't been shown in the last 7 days
 */
export const shouldShowResultsPopup = (): boolean => {
  return !hasOptedIntoWaitlist() && !wasResultsPopupShownRecently();
};

/**
 * Clears all PPC-related cookies (for testing)
 */
export const clearAllPPCCookies = (): void => {
  deleteCookie(CORTEXIQ_PROMO_COOKIE);
  deleteCookie('ppc_waitlist_seen');
  deleteCookie('ppc_waitlist_opted_in');
  deleteCookie('ppc_results_popup_shown');
};
